import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { errorMessage } from "../first-round";
import {
  nextSort,
  pageCount,
  rangeLabel,
  type SortState,
  type SortValue,
  sortRows,
} from "./data-table";

export type Column<T> = {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /** Makes the column sortable by this value. */
  sortValue?: (row: T) => SortValue;
  className?: string;
};

type DataTableProps<T> = {
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  isLoading?: boolean;
  error?: unknown;
  emptyMessage?: ReactNode;
  /** Rows per page; null shows every row (for short tables or server-paged ones). */
  pageSize?: number | null;
  initialSort?: SortState;
  onRowClick?: (row: T) => void;
  /** Accessible name for the table. */
  label: string;
};

/**
 * The admin console's table: sortable headers, client-side paging, and loading, error and empty
 * states, so every list page behaves the same.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  isLoading,
  error,
  emptyMessage = "Nothing to show.",
  pageSize = 25,
  initialSort = null,
  onRowClick,
  label,
}: DataTableProps<T>) {
  const [sort, setSort] = useState<SortState>(initialSort);
  const [page, setPage] = useState(0);

  const sorted = useMemo(() => {
    const column = sort && columns.find((c) => c.key === sort.key);
    return column ? sortRows(rows ?? [], column.sortValue, sort.direction) : rows ?? [];
  }, [rows, sort, columns]);

  const total = sorted.length;
  const pages = pageSize ? pageCount(total, pageSize) : 1;
  // New results (a search, a refetch) start again from the first page.
  useEffect(() => setPage(0), [rows]);
  const current = Math.min(page, pages - 1);
  const visible = pageSize ? sorted.slice(current * pageSize, (current + 1) * pageSize) : sorted;

  const status = isLoading ? "Loading…" : error ? null : total === 0 ? emptyMessage : null;

  return (
    <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
      <Table aria-label={label} aria-busy={isLoading || undefined}>
        <TableHeader>
          <TableRow>
            {columns.map((column) => {
              const active = sort?.key === column.key ? sort.direction : null;
              return (
                <TableHead
                  key={column.key}
                  className={cn("h-10 px-3", column.className)}
                  aria-sort={
                    active === "asc" ? "ascending" : active === "desc" ? "descending" : undefined
                  }
                >
                  {column.sortValue ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 uppercase hover:text-slate-900"
                      onClick={() => {
                        setSort((s) => nextSort(s, column.key));
                        setPage(0);
                      }}
                    >
                      {column.header}
                      {active === "asc" ? (
                        <ArrowUp className="h-3 w-3" />
                      ) : active === "desc" ? (
                        <ArrowDown className="h-3 w-3" />
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-40" />
                      )}
                    </button>
                  ) : (
                    column.header
                  )}
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {error ? (
            <TableRow>
              <TableCell colSpan={columns.length} className="px-3 py-6 text-center text-red-600">
                Couldn't load this list: {errorMessage(error)}
              </TableCell>
            </TableRow>
          ) : status ? (
            <TableRow>
              <TableCell colSpan={columns.length} className="px-3 py-8 text-center text-slate-500">
                {status}
              </TableCell>
            </TableRow>
          ) : (
            visible.map((row) => (
              <TableRow
                key={rowKey(row)}
                className={cn(onRowClick && "cursor-pointer")}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((column) => (
                  <TableCell key={column.key} className={cn("px-3 py-2.5", column.className)}>
                    {column.cell(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {pageSize && !error && total > pageSize && (
        <div className="flex items-center justify-between border-t border-slate-200 px-3 py-2 text-xs text-slate-600">
          <span>{rangeLabel(current, pageSize, total)}</span>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              aria-label="Previous page"
              disabled={current === 0}
              onClick={() => setPage(current - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              aria-label="Next page"
              disabled={current >= pages - 1}
              onClick={() => setPage(current + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
